import { DataTable } from "@/components/ui/datatable";
import { RowSelectionState } from "@tanstack/react-table";
import { ComponentProps, useCallback, useMemo, useState } from "react";

type DataTableSelection<TData> = NonNullable<
  ComponentProps<typeof DataTable<TData, unknown>>["selection"]
>;

type UseDashboardRowSelectionOptions = {
  getAllRowIds?: () => Promise<{ id: string }[]>;
};

export function useDashboardRowSelection<TData extends { id: string }>(
  options?: UseDashboardRowSelectionOptions,
) {
  const [selectionState, setSelectionState] = useState<RowSelectionState>({});

  const dataTableSelection: DataTableSelection<TData> = useMemo(
    () => ({
      getRowId: (row) => row.id,
      state: {
        selectionState,
        setSelectionState,
      },
      enableSelectAll: true,
      ...(options?.getAllRowIds ? { getAllRowIds: options.getAllRowIds } : {}),
    }),
    [selectionState, options?.getAllRowIds],
  );

  const clearSelection = () => setSelectionState({});

  const isSelected = useCallback(
    (id: string) => selectionState[id] === true,
    [selectionState],
  );

  /**
   * Add or remove a single row from the selection. The key is deleted when
   * unselected so the shape stays consistent with the datatable selection
   * (the selection bar counts every key present).
   */
  const setRowSelected = useCallback((id: string, selected: boolean) => {
    setSelectionState((prev) => {
      if (selected) {
        if (prev[id] === true) return prev;
        return { ...prev, [id]: true };
      }
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const toggleSelected = useCallback(
    (id: string) => setRowSelected(id, selectionState[id] !== true),
    [setRowSelected, selectionState],
  );

  return {
    selectionState,
    setSelectionState,
    dataTableSelection,
    clearSelection,
    isSelected,
    setRowSelected,
    toggleSelected,
  };
}
